import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import UserAvatar from "@/components/UserAvatar";
import UploadButton from "@/components/UploadButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronUp, ArrowLeft, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2.5 border-t border-border/60 first:border-t-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right break-words">{value || "—"}</span>
    </div>
  );
}
function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

function Section({ id, title, children, ctx, editable = true }) {
  const { isManager, editing, open, setEditing, setOpen, cancel, save, saving, t } = ctx;
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={`section-${id}`}>
      <div className="flex items-center justify-between px-5 py-4">
        <h2 className="font-display text-lg font-bold text-primary">{title}</h2>
        <div className="flex items-center gap-2">
          {isManager && editable && (editing === id ? (
            <>
              <Button size="sm" variant="ghost" onClick={cancel}>{t("cancel")}</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-section">{t("save")}</Button>
            </>
          ) : (
            <button className="text-sm text-primary font-medium hover:underline" onClick={() => { setEditing(id); setOpen((o) => ({ ...o, [id]: true })); }} data-testid={`edit-${id}`}>{t("edit")}</button>
          ))}
          <button onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))} className="w-7 h-7 rounded-full hover:bg-accent flex items-center justify-center" data-testid={`toggle-${id}`}>
            <ChevronUp className={`w-4 h-4 transition-transform ${open[id] ? "" : "rotate-180"}`} />
          </button>
        </div>
      </div>
      {open[id] && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

export default function EmployeeDetail({ employee, isManager, isAdmin, canDelete, onSaved, onDelete, onBack }) {
  const { t } = useT();
  const [form, setForm] = useState(employee);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState({ personal: true, access: true, contact: true, positions: true, health: true, certs: true, emergency: true, notes: true });
  const [saving, setSaving] = useState(false);
  const [positions, setPositions] = useState([]);

  useEffect(() => { api.get("/positions").then((r) => setPositions(r.data)).catch(() => {}); }, []);
  useEffect(() => { setForm(employee); setEditing(null); }, [employee]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const posById = Object.fromEntries(positions.map((p) => [p.id, p]));
  const togglePos = (pid) => setForm((f) => {
    const cur = f.position_ids || [];
    const next = cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid];
    const primary = next.includes(f.primary_position_id) ? f.primary_position_id : (next[0] || null);
    return { ...f, position_ids: next, primary_position_id: primary };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await api.put(`/employees/${employee.id}`, payload);
      toast.success(t("save")); setEditing(null); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const cancel = () => { setForm(employee); setEditing(null); };
  const sectionCtx = { isManager, editing, open, setEditing, setOpen, cancel, save, saving, t };

  const e = form;
  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4">
      <div className="flex items-center gap-3">
        <button className="md:hidden" onClick={onBack} data-testid="detail-back"><ArrowLeft className="w-5 h-5" /></button>
        <UserAvatar name={e.name} src={e.profile_picture} size={64} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold truncate">{e.name}</h1>
            <Badge variant="outline" className="capitalize text-[10px]">{t(e.role)}</Badge>
            {!e.active && <Badge variant="secondary" className="text-[10px]">{t("inactive")}</Badge>}
          </div>
          {(() => { const pp = posById[e.primary_position_id]; return pp
            ? <p className="text-sm font-medium" style={{ color: pp.color }}><span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: pp.color }} />{pp.name}</p>
            : <p className="text-sm text-muted-foreground">{e.position || "—"}</p>; })()}
        </div>
        {canDelete && <Button variant="ghost" size="icon" onClick={() => onDelete(employee.id)} data-testid="delete-employee"><Trash2 className="w-4 h-4 text-destructive" /></Button>}
      </div>

      {/* Personal */}
      <Section ctx={sectionCtx} id="personal" title={t("personalInfo")}>
        {editing === "personal" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <UserAvatar name={`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.name} src={e.profile_picture} size={48} />
              <UploadButton label={t("profilePicture")} accept="image/*" category="avatar" testId="upload-avatar" onUploaded={(d) => set("profile_picture", `file:${d.id}`)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fld label={t("firstName")}><Input value={e.first_name || ""} onChange={(ev) => set("first_name", ev.target.value)} data-testid="emp-first-name" /></Fld>
              <Fld label={t("lastName")}><Input value={e.last_name || ""} onChange={(ev) => set("last_name", ev.target.value)} data-testid="emp-last-name" /></Fld>
              <Fld label={t("dateOfBirth")}><Input type="date" value={e.date_of_birth || ""} onChange={(ev) => set("date_of_birth", ev.target.value)} /></Fld>
              <Fld label={t("role")}>
                <Select value={e.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="employee">{t("employee")}</SelectItem><SelectItem value="supervisor">{t("supervisor")}</SelectItem><SelectItem value="admin">{t("admin")}</SelectItem></SelectContent>
                </Select>
              </Fld>
              <Fld label={t("employmentStatus")}>
                <Select value={e.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="full_time">{t("fullTime")}</SelectItem><SelectItem value="part_time">{t("partTime")}</SelectItem><SelectItem value="casual">{t("casual")}</SelectItem></SelectContent>
                </Select>
              </Fld>
              <Fld label={`${t("hourlyRate")} (CAD)`}>
                <Input type="number" step="0.01" min="0" value={e.hourly_rate ?? ""} disabled={!isAdmin}
                  onChange={(ev) => set("hourly_rate", ev.target.value === "" ? null : parseFloat(ev.target.value))}
                  placeholder="0.00" data-testid="emp-hourly-rate" />
                {!isAdmin && <p className="text-[11px] text-muted-foreground mt-1">{t("adminOnlyField")}</p>}
              </Fld>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!e.active} onChange={(ev) => set("active", ev.target.checked)} className="w-4 h-4" data-testid="emp-active" />{t("active")}</label>
          </div>
        ) : (
          <>
            <Row label={t("fullName")} value={e.name} />
            <Row label={t("dateOfBirth")} value={e.date_of_birth} />
            <Row label={t("role")} value={t(e.role)} />
            <Row label={t("employmentStatus")} value={t(e.employment_status === "full_time" ? "fullTime" : e.employment_status === "part_time" ? "partTime" : "casual")} />
            <Row label={`${t("hourlyRate")} (CAD)`} value={e.hourly_rate != null ? `$${Number(e.hourly_rate).toFixed(2)} / h` : "—"} />
          </>
        )}
      </Section>

      {/* App access */}
      <Section ctx={sectionCtx} id="access" title={t("appAccess")}>
        {editing === "access" ? (
          <div className="grid grid-cols-2 gap-3">
            <Fld label={t("email")}>
              <Input type="email" value={e.email || ""} disabled={!isAdmin} onChange={(ev) => set("email", ev.target.value)} data-testid="emp-email" />
              {!isAdmin && <p className="text-[11px] text-muted-foreground mt-1">{t("adminOnlyField")}</p>}
            </Fld>
            <Fld label={t("newPassword")}><Input type="password" value={e.password || ""} onChange={(ev) => set("password", ev.target.value)} placeholder="••••" data-testid="emp-password" /></Fld>
          </div>
        ) : (
          <Row label={t("email")} value={e.email} />
        )}
      </Section>

      {/* Contact */}
      <Section ctx={sectionCtx} id="contact" title={t("contactInfo")}>
        {editing === "contact" ? (
          <div className="grid grid-cols-2 gap-3">
            <Fld label={t("position")}><Input value={e.position || ""} onChange={(ev) => set("position", ev.target.value)} data-testid="emp-position" /></Fld>
            <Fld label={t("phone")}><Input value={e.phone || ""} onChange={(ev) => set("phone", ev.target.value)} /></Fld>
            <Fld label={t("dateOfHire")}><Input type="date" value={e.date_of_hire || ""} onChange={(ev) => set("date_of_hire", ev.target.value)} /></Fld>
            <Fld label={t("nas")}><Input value={e.nas || ""} onChange={(ev) => set("nas", ev.target.value)} /></Fld>
            <div className="col-span-2"><Fld label={t("address")}><Input value={e.address || ""} onChange={(ev) => set("address", ev.target.value)} /></Fld></div>
          </div>
        ) : (
          <>
            <Row label={t("email")} value={e.email} />
            <Row label={t("position")} value={e.position} />
            <Row label={t("phone")} value={e.phone} />
            <Row label={t("dateOfHire")} value={e.date_of_hire} />
            <Row label={t("nas")} value={e.nas} />
            <Row label={t("address")} value={e.address} />
          </>
        )}
      </Section>

      {/* Positions */}
      <Section ctx={sectionCtx} id="positions" title={t("assignedPositions")}>
        {editing === "positions" ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="emp-positions-picker">
              {positions.filter((p) => p.active || (e.position_ids || []).includes(p.id)).map((p) => {
                const on = (e.position_ids || []).includes(p.id);
                return (
                  <button key={p.id} onClick={() => togglePos(p.id)} data-testid={`emp-position-${p.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? "text-white border-transparent" : "border-border text-muted-foreground"}`}
                    style={on ? { background: p.color } : {}}>
                    <span className="w-2 h-2 rounded-full" style={{ background: on ? "#fff" : p.color }} />{p.name}
                  </button>
                );
              })}
              {positions.length === 0 && <p className="text-sm text-muted-foreground">{t("noPositions")}</p>}
            </div>
            {(e.position_ids || []).length > 0 && (
              <div className="space-y-1.5 max-w-xs"><Label className="text-xs">{t("primaryPosition")}</Label>
                <Select value={e.primary_position_id || ""} onValueChange={(v) => set("primary_position_id", v)}>
                  <SelectTrigger data-testid="emp-primary-position"><SelectValue /></SelectTrigger>
                  <SelectContent>{(e.position_ids || []).map((pid) => posById[pid] && <SelectItem key={pid} value={pid}>{posById[pid].name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 py-1" data-testid="emp-positions-view">
            {(e.position_ids || []).length ? (e.position_ids || []).map((pid) => posById[pid] && (
              <span key={pid} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${posById[pid].color}22`, color: posById[pid].color }}>
                <span className="w-2 h-2 rounded-full" style={{ background: posById[pid].color }} />{posById[pid].name}{pid === e.primary_position_id && " ★"}
              </span>
            )) : <p className="text-sm text-muted-foreground">—</p>}
          </div>
        )}
      </Section>

      {/* Health */}
      <Section ctx={sectionCtx} id="health" title={t("healthInfo")}>
        {editing === "health"
          ? <Textarea rows={4} value={e.health_info || ""} onChange={(ev) => set("health_info", ev.target.value)} placeholder={t("healthPlaceholder")} data-testid="emp-health" />
          : <p className="text-sm whitespace-pre-wrap py-1">{e.health_info || "—"}</p>}
      </Section>

      {/* Certifications */}
      <Section ctx={sectionCtx} id="certs" title={t("certifications")}>
        {editing === "certs" ? (
          <div className="space-y-2">
            {(e.certifications || []).map((c, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Fld label={t("certName")}><Input value={c.name} onChange={(ev) => { const cs = [...e.certifications]; cs[i] = { ...cs[i], name: ev.target.value }; set("certifications", cs); }} /></Fld>
                <Fld label={t("expiryDate")}><Input type="date" value={c.expiry_date || ""} onChange={(ev) => { const cs = [...e.certifications]; cs[i] = { ...cs[i], expiry_date: ev.target.value }; set("certifications", cs); }} /></Fld>
                <Button variant="ghost" size="icon" onClick={() => set("certifications", e.certifications.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => set("certifications", [...(e.certifications || []), { name: "", expiry_date: "" }])} data-testid="add-cert"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          </div>
        ) : (
          (e.certifications || []).length ? e.certifications.map((c, i) => {
            const expired = c.expiry_date && new Date(c.expiry_date) < new Date();
            return <Row key={i} label={c.name} value={<span className={expired ? "text-destructive" : "text-emerald-600"}>{c.expiry_date || "—"}</span>} />;
          }) : <p className="text-sm text-muted-foreground py-2">—</p>
        )}
      </Section>

      {/* Emergency contacts */}
      <Section ctx={sectionCtx} id="emergency" title={t("emergencyContacts")}>
        {editing === "emergency" ? (
          <div className="space-y-2">
            {(e.emergency_contacts || []).map((c, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Fld label={t("contactName")}><Input value={c.name} onChange={(ev) => { const cs = [...e.emergency_contacts]; cs[i] = { ...cs[i], name: ev.target.value }; set("emergency_contacts", cs); }} /></Fld>
                <Fld label={t("relationship")}><Input value={c.relationship || ""} onChange={(ev) => { const cs = [...e.emergency_contacts]; cs[i] = { ...cs[i], relationship: ev.target.value }; set("emergency_contacts", cs); }} /></Fld>
                <Fld label={t("phone")}><Input value={c.phone || ""} onChange={(ev) => { const cs = [...e.emergency_contacts]; cs[i] = { ...cs[i], phone: ev.target.value }; set("emergency_contacts", cs); }} /></Fld>
                <Button variant="ghost" size="icon" onClick={() => set("emergency_contacts", e.emergency_contacts.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => set("emergency_contacts", [...(e.emergency_contacts || []), { name: "", relationship: "", phone: "" }])} data-testid="add-contact"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          </div>
        ) : (
          (e.emergency_contacts || []).length ? e.emergency_contacts.map((c, i) => <Row key={i} label={`${c.name}${c.relationship ? ` (${c.relationship})` : ""}`} value={c.phone} />) : <p className="text-sm text-muted-foreground py-2">—</p>
        )}
      </Section>

      {/* Notes */}
      <Section ctx={sectionCtx} id="notes" title={t("notes")}>
        {editing === "notes"
          ? <Textarea rows={3} value={e.notes || ""} onChange={(ev) => set("notes", ev.target.value)} />
          : <p className="text-sm whitespace-pre-wrap py-1">{e.notes || "—"}</p>}
      </Section>
    </div>
  );
}
